import { supabase } from './supabase';

/**
 * All backend queries live here. Each returns { data, error }.
 * Category param is always one of 'ALL' | 'FOOD' | 'TREATS'.
 */

// ---------- Meta / brand universe ----------
export async function fetchMeta() {
  const { data, error } = await supabase.from('meta').select('key,value');
  if (error) return { data: null, error };
  const map = {};
  data.forEach(r => (map[r.key] = r.value));
  return { data: map, error: null };
}

export async function fetchBrands() {
  const { data, error } = await supabase
    .from('brand_universe')
    .select('group_flag,brand,is_named,rank')
    .order('rank', { ascending: true });
  if (error) return { data: null, error };
  const own = data.filter(r => r.group_flag === 'OWN').map(r => r.brand);
  const p3Named = data.filter(r => r.group_flag === 'P3' && r.is_named).map(r => r.brand);
  return { data: { own, p3: [...p3Named, 'Other 3P'] }, error: null };
}

// ---------- Regions ----------
export async function fetchRegions() {
  // Distinct regions, cheaply (one slice of the region cohorts table)
  const { data, error } = await supabase
    .from('cohorts_region')
    .select('region')
    .eq('category', 'ALL').eq('channel', 'Omni').eq('month_index', 0);
  if (error) return { data: [], error };
  return { data: [...new Set(data.map(r => r.region))].sort(), error: null };
}

// Region-wise cohorts (all brands) — same shape as fetchCohorts.
export async function fetchCohortsRegion({ category = 'ALL', region }) {
  const { data, error } = await supabase
    .from('cohorts_region')
    .select('channel,cohort_month,cohort_size,month_index,active_customers,rate')
    .eq('category', category)
    .eq('region', region)
    .order('cohort_month', { ascending: true });
  if (error) return { data: null, error };
  const grouped = { Omni: {}, Retail: {}, 'E-Commerce': {} };
  for (const row of data) {
    const g = grouped[row.channel];
    if (!g) continue;
    const label = row.cohort_month.slice(0, 7);
    if (!g[label]) g[label] = { label, size: row.cohort_size, cells: new Array(13).fill(null) };
    g[label].cells[row.month_index] = { active: row.active_customers, rate: Number(row.rate) };
  }
  const shape = {};
  for (const ch of Object.keys(grouped)) {
    shape[ch] = Object.values(grouped[ch]).sort((a, b) => (a.label < b.label ? -1 : 1));
  }
  return { data: shape, error: null };
}

// ---------- Cohorts ----------
export async function fetchCohorts({ category = 'ALL', entityKey = 'ALL' }) {
  // Returns all channels for this entity/category
  const { data, error } = await supabase
    .from('cohorts')
    .select('channel,cohort_month,cohort_size,month_index,active_customers,rate')
    .eq('category', category)
    .eq('entity_key', entityKey)
    .order('cohort_month', { ascending: true });
  if (error) return { data: null, error };
  // Reshape into { All: [{label,size,cells}], Retail: [...], ... }
  const grouped = { Omni: {}, Retail: {}, 'E-Commerce': {} };
  for (const row of data) {
    const g = grouped[row.channel];
    if (!g) continue;
    const label = row.cohort_month.slice(0, 7); // YYYY-MM
    if (!g[label]) g[label] = { label, size: row.cohort_size, cells: new Array(13).fill(null) };
    g[label].cells[row.month_index] = { active: row.active_customers, rate: Number(row.rate) };
  }
  const shape = {};
  for (const ch of Object.keys(grouped)) {
    shape[ch] = Object.values(grouped[ch]).sort((a, b) => (a.label < b.label ? -1 : 1));
  }
  return { data: shape, error: null };
}

// ---------- Trend + Impact ----------
export async function fetchCohortTrend(category = 'ALL') {
  const { data, error } = await supabase
    .from('cohort_trend')
    .select('cohort_month,cohort_size,m1_pct,m3_pct,acq_disc_pct,excluded')
    .eq('category', category)
    .order('cohort_month', { ascending: true });
  if (error) return { data: null, error };
  return {
    data: data.map(r => ({
      label: r.cohort_month.slice(0, 7),
      size: r.cohort_size,
      m1_pct: r.m1_pct == null ? null : Number(r.m1_pct),
      m3_pct: r.m3_pct == null ? null : Number(r.m3_pct),
      acq_disc_pct: r.acq_disc_pct == null ? null : Number(r.acq_disc_pct),
      excluded: r.excluded,
    })),
    error: null,
  };
}

export async function fetchTargets(category = 'ALL') {
  const { data, error } = await supabase.from('targets').select('*').eq('category', category);
  if (error) return { data: null, error };
  const map = {};
  data.forEach(r => {
    map[r.metric] = {
      baseline: r.baseline == null ? null : Number(r.baseline),
      final: Number(r.final_target),
      quarterly: [r.q1_milestone, r.q2_milestone, r.q3_milestone, r.q4_milestone]
        .filter(v => v != null)
        .map(v => Number(v)),
    };
  });
  return { data: map, error: null };
}

export async function fetchGuardrail(category = 'ALL') {
  const { data, error } = await supabase
    .from('guardrail').select('month,disc_pct').eq('category', category)
    .order('month', { ascending: true });
  if (error) return { data: null, error };
  return { data: data.map(r => ({ month: r.month.slice(0, 7), disc_pct: Number(r.disc_pct) })), error: null };
}

export async function fetchNewVsReturning(category = 'ALL') {
  const { data, error } = await supabase
    .from('new_vs_returning').select('month,total,new_cust,returning,new_pct')
    .eq('category', category).order('month', { ascending: true });
  if (error) return { data: null, error };
  return {
    data: data.map(r => ({
      month: r.month.slice(0, 7),
      total: r.total, new: r.new_cust, returning: r.returning, new_pct: Number(r.new_pct),
    })),
    error: null,
  };
}

// ---------- Customers (Targeting tab + Export) ----------
/**
 * Filter customers server-side. Returns row count via HEAD query when countOnly=true.
 * For actual rows, use a small limit (dashboard just needs aggregates + export).
 */
export async function queryCustomers({ category = null, channel = null, segment = null,
                                       crossSellOnly = false, reachDays = null, region = null,
                                       limit = null, countOnly = false }) {
  let q = supabase.from('customers').select(countOnly ? 'customer_id' : '*',
    countOnly ? { count: 'exact', head: true } : { count: 'exact' });

  if (category === 'FOOD')   q = q.like('categories_ever', '%Food%');
  if (category === 'TREATS') q = q.like('categories_ever', '%Treats%');
  if (channel && channel !== 'All') q = q.eq('lifetime_channel_seg', channel);
  if (segment) q = q.eq('segment', segment);
  if (crossSellOnly) q = q.eq('cross_sell_flag', 1);
  if (reachDays != null) q = q.lte('recency_days', reachDays);
  if (region && region !== 'ALL') q = q.eq('region', region);
  if (limit) q = q.limit(limit);

  return await q;
}

/**
 * Fetch aggregate counts (segment breakdown, action breakdown, cross-sell count)
 * for the Targeting tab, respecting all filters, without shipping every row.
 * Uses a Postgres RPC we'll define; falls back to client-side aggregation if not present.
 */
export async function fetchTargetingSummary(filters) {
  // Uses RPC if available (see 05_rpc.sql). Falls back to a bulk fetch.
  const { data, error } = await supabase.rpc('targeting_summary', {
    p_category: filters.category ?? null,
    p_channel: (!filters.channel || filters.channel === 'Omni') ? null : filters.channel,
    p_reach_days: filters.reachDays ?? null,
    p_cross_sell_only: filters.crossSellOnly ?? false,
    p_region: (filters.region && filters.region !== 'ALL') ? filters.region : null,
  });
  if (error) return { data: null, error };
  return { data, error: null };
}

// ---------- Cohort selector helper: enumerate available cohorts ----------
export async function fetchCohortLabels(category = 'ALL', entityKey = 'ALL') {
  const { data, error } = await supabase
    .from('cohorts')
    .select('cohort_month', { count: 'exact' })
    .eq('category', category).eq('entity_key', entityKey).eq('channel', 'All').eq('month_index', 0)
    .order('cohort_month', { ascending: true });
  if (error) return { data: null, error };
  return { data: [...new Set(data.map(r => r.cohort_month.slice(0, 7)))], error: null };
}
